/* ═══════════════════════════════════════════════════════════════
   Deliverables widget — card face (compact counts / standard list +
   quick add + per-row pencil) plus the full-bleed takeover (stats +
   Gantt timeline + full inline editor), moved verbatim in behavior
   from the pre-R1 dashboard-widgets module (deliverables-widget.tsx,
   quick-add :100-162, timeline :294-503, inline edits :569-694).
   Manage opens panels/DeliverablesPanel.tsx — the same editor table
   this file exports, in a Sheet instead of full-bleed.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Pencil, Settings2, Maximize2, Check } from 'lucide-react';
import type { Deliverable } from '../../../../shared/entities';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { WidgetFrame, WidgetActionButton, WList } from '../../../components/widgets';
import type { WListRow } from '../../../components/widgets';
import { rem } from '../../../lib/rem';
import {
  COLOR_OK,
  COLOR_WARN,
  COLOR_DANGER,
  COLOR_INFO,
  COLOR_NEUTRAL,
  todayIso,
  daysBetween,
  shortDate,
  subText,
  bigNum,
  statRow,
  sectionLabel,
  btnPrimary,
  KpiTile,
  countChip,
} from './shared';
import { DeliverablesPanel } from '../panels/DeliverablesPanel';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
];

function statusColor(d: Deliverable, today: string) {
  if (d.status === 'complete') return COLOR_OK;
  const days = d.dueDate ? daysBetween(today, d.dueDate) : null;
  if (days === null) return COLOR_NEUTRAL;
  if (days < 0) return COLOR_DANGER;
  if (days < 7) return COLOR_WARN;
  return COLOR_INFO;
}

function dueLabel(d: Deliverable, today: string): { text: string; color: string; bold: boolean } {
  if (d.status === 'complete') return { text: 'Done', color: COLOR_OK, bold: false };
  if (!d.dueDate) return { text: 'no date', color: 'var(--tm)', bold: false };
  const days = daysBetween(today, d.dueDate);
  if (days < 0) return { text: `${Math.abs(days)}d late`, color: COLOR_DANGER, bold: true };
  if (days < 7) return { text: `${days}d`, color: COLOR_WARN, bold: false };
  return { text: `${days}d`, color: 'var(--tm)', bold: false };
}

/** Ghost add row — the zero-friction path: a deliverable is just a name +
 *  optional date. Shared by the card face, the takeover editor, and the
 *  Manage panel. */
export function QuickAddDeliverable({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [newName, setNewName] = useState('');
  const [newDue, setNewDue] = useState('');
  const [saving, setSaving] = useState(false);
  if (!ctx.can.edit) return null;

  const addNew = async () => {
    if (!newName.trim() || saving) return;
    setSaving(true);
    try {
      await ctx.saveDeliverable({
        id: '',
        projectId: ctx.project.id,
        name: newName.trim(),
        description: '',
        dueDate: newDue || undefined,
        status: 'pending',
        issued: false,
        createdAt: '',
        updatedAt: '',
      } as Deliverable);
      setNewName('');
      setNewDue('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        alignItems: 'center',
        minHeight: rem(32),
        padding: '0 var(--space-2)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <Icon
        icon={Plus}
        size="md"
        aria-hidden="true"
        style={{ color: 'var(--tm)', flexShrink: 0 }}
      />
      <input
        type="text"
        placeholder="Add deliverable…"
        aria-label="New deliverable name"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') addNew();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--font-size-base)',
          fontFamily: 'var(--font-sans)',
          border: 'none',
          background: 'transparent',
          color: 'var(--tx)',
          padding: 'var(--space-1) 0',
        }}
      />
      <input
        type="date"
        aria-label="New deliverable due date"
        value={newDue}
        onChange={(e) => setNewDue(e.target.value)}
        style={{
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-sans)',
          border: 'none',
          background: 'transparent',
          color: newDue ? 'var(--tx)' : 'var(--tm)',
          padding: 'var(--space-1) 0',
        }}
      />
      {newName.trim() && (
        <button
          onClick={addNew}
          disabled={saving}
          aria-label="Add deliverable"
          style={btnPrimary(saving)}
        >
          Add
        </button>
      )}
    </div>
  );
}

// Success tone lives in the tint + dot, never in the text color — green
// text on a green tint reads 3.45:1 in light theme (fails DESIGN.md §7).
// Text is `--tx` (body contrast, passes both themes).
const issuedBadge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: rem(4),
  fontSize: 'var(--font-size-xs)',
  padding: `1px ${rem(4)}`,
  borderRadius: 'var(--radius-sm)',
  background: 'color-mix(in srgb, var(--ok) 12%, transparent)',
  color: 'var(--tx)',
  fontWeight: 700,
  flexShrink: 0,
};
const issuedDot: React.CSSProperties = {
  width: rem(6),
  height: rem(6),
  borderRadius: 'var(--radius-full)',
  background: 'var(--ok)',
  flexShrink: 0,
};

/** Inline name/date/status editor — replaces a WList row's `primary` slot
 *  while that deliverable is being edited. Enter or the check commits; Esc
 *  or the x cancels. Ported verbatim from the pre-WList `DeliverableRow`
 *  editing branch. */
function DeliverableEditForm({
  ctx,
  d,
  onDone,
}: {
  ctx: ProjectWidgetCtx;
  d: Deliverable;
  onDone: () => void;
}) {
  const [name, setName] = useState(d.name);
  const [due, setDue] = useState(d.dueDate || '');
  const [status, setStatus] = useState<Deliverable['status']>(d.status);
  const [error, setError] = useState<string | null>(null);

  const commit = async () => {
    setError(null);
    try {
      await ctx.saveDeliverable({
        ...d,
        name: name.trim() || d.name,
        dueDate: due || undefined,
        status,
      });
      onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', width: '100%' }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDone();
        if (e.key === 'Enter') void commit();
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: 1, height: rem(30) }}
        />
        <Input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          style={{ height: rem(30), width: rem(140) }}
        />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <SearchableSelect
            value={status}
            options={STATUS_OPTIONS}
            onChange={(v) => setStatus(v as Deliverable['status'])}
            clearable={false}
          />
        </div>
        <Button size="icon" variant="ghost" aria-label="Save" onClick={commit}>
          <Icon icon={Check} size="sm" />
        </Button>
        <Button size="icon" variant="ghost" aria-label="Cancel" onClick={onDone}>
          <Icon icon={X} size="sm" />
        </Button>
      </div>
      {error && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--dg)' }}>{error}</div>}
    </div>
  );
}

/** Builds one WList row for a deliverable — read view (status lead, name +
 *  issued badge, due meta, pencil trailing), or (while `editingId` matches)
 *  the inline edit form standing in for the whole row. */
function buildDeliverableRow(
  ctx: ProjectWidgetCtx,
  d: Deliverable,
  today: string,
  editingId: string | null,
  setEditingId: (id: string | null) => void
): WListRow {
  if (editingId === d.id) {
    return {
      id: d.id,
      primary: <DeliverableEditForm ctx={ctx} d={d} onDone={() => setEditingId(null)} />,
    };
  }
  const dl = dueLabel(d, today);
  return {
    id: d.id,
    lead: (
      <span
        aria-hidden="true"
        style={{
          width: rem(8),
          height: rem(8),
          borderRadius: 'var(--radius-full)',
          background: statusColor(d, today),
          flexShrink: 0,
        }}
      />
    ),
    primary: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span>{d.name}</span>
        {d.issued && (
          <span style={issuedBadge}>
            <span aria-hidden="true" style={issuedDot} />
            ISSUED
          </span>
        )}
      </span>
    ),
    meta: (
      <span style={{ display: 'inline-flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <span>{d.dueDate ? shortDate(d.dueDate) : '—'}</span>
        <span style={{ color: dl.color, fontWeight: dl.bold ? 700 : 400 }}>{dl.text}</span>
      </span>
    ),
    onClick: () => ctx.onDrill({ kind: 'deliverable', deliverableId: d.id }),
    trailing: ctx.can.edit ? (
      <span onClick={(e) => e.stopPropagation()}>
        <WidgetActionButton
          icon={Pencil}
          label={`Edit ${d.name}`}
          onClick={() => setEditingId(d.id)}
        />
      </span>
    ) : undefined,
  };
}

function DeliverablesCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [manageOpen, setManageOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const deliverables = ctx.deliverables.data;
  const today = todayIso();

  const sorted = useMemo(
    () =>
      [...deliverables].sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999')),
    [deliverables]
  );
  const overdue = sorted.filter(
    (d) => d.status !== 'complete' && d.dueDate && daysBetween(today, d.dueDate) < 0
  );
  const upcoming = sorted.filter(
    (d) => d.status !== 'complete' && (!d.dueDate || daysBetween(today, d.dueDate) >= 0)
  );
  const complete = sorted.filter((d) => d.status === 'complete');
  // Compact tier's one headline stat (T3a superset merge, coordinator
  // correction 2026-09-28: this was VISIBLE content in the old compact
  // view, not just computed — kept as a summary row above the list, not
  // dropped in favor of the chips row alone).
  const nextDue = upcoming.find((d) => d.dueDate);
  const worstOverdue = overdue[0];

  const frameStatus =
    ctx.deliverables.status === 'loading'
      ? 'loading'
      : ctx.deliverables.status === 'error'
        ? 'error'
        : deliverables.length === 0
          ? 'empty'
          : 'ready';

  return (
    <>
      <WidgetFrame
        title="Deliverables"
        required
        form="list"
        status={frameStatus}
        error={{
          message: ctx.deliverables.error || 'Could not load deliverables.',
          onRetry: () => void ctx.deliverables.reload(),
        }}
        empty={{
          title: 'No deliverables yet',
          subtitle: ctx.can.edit ? undefined : 'Ask a project editor to add one.',
          action: ctx.can.edit
            ? { label: 'Add one', onClick: () => setManageOpen(true) }
            : undefined,
        }}
        actions={
          <>
            {ctx.can.edit && (
              <WidgetActionButton
                icon={Settings2}
                label="Manage deliverables"
                onClick={() => setManageOpen(true)}
              />
            )}
            <WidgetActionButton
              icon={Maximize2}
              label="Open deliverables"
              onClick={() => ctx.openTakeover('deliverables')}
            />
          </>
        }
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            padding: 'var(--space-1)',
          }}
        >
          {overdue.length > 0 ? (
            <KpiTile
              value={String(overdue.length)}
              label={`overdue${nextDue ? ` · next: ${nextDue.name}` : worstOverdue ? ` · ${worstOverdue.name}` : ''}`}
              color={COLOR_DANGER}
              onClick={() =>
                worstOverdue && ctx.onDrill({ kind: 'deliverable', deliverableId: worstOverdue.id })
              }
              ariaLabel={`${overdue.length} overdue deliverables. View deliverables`}
            />
          ) : nextDue && nextDue.dueDate ? (
            (() => {
              const days = daysBetween(today, nextDue.dueDate as string);
              return (
                <KpiTile
                  value={`${days}d`}
                  label={`until ${nextDue.name}`}
                  color={days < 7 ? COLOR_WARN : COLOR_INFO}
                  onClick={() => ctx.onDrill({ kind: 'deliverable', deliverableId: nextDue.id })}
                  ariaLabel={`${days} days until ${nextDue.name}. View deliverables`}
                />
              );
            })()
          ) : (
            <KpiTile value={String(upcoming.length)} label="upcoming deliverables" />
          )}
          {(overdue.length > 0 || upcoming.length > 0 || complete.length > 0) && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {overdue.length > 0 && (
                <span style={countChip(COLOR_DANGER)}>{overdue.length} overdue</span>
              )}
              {upcoming.length > 0 && (
                <span style={countChip(COLOR_INFO)}>{upcoming.length} upcoming</span>
              )}
              {complete.length > 0 && (
                <span style={countChip(COLOR_OK)}>{complete.length} complete</span>
              )}
            </div>
          )}
          <WList
            rows={sorted.map((d) => buildDeliverableRow(ctx, d, today, editingId, setEditingId))}
            max={Math.max(sorted.length, 1)}
          />
          <QuickAddDeliverable ctx={ctx} />
        </div>
      </WidgetFrame>
      <DeliverablesPanel ctx={ctx} open={manageOpen} onClose={() => setManageOpen(false)} />
    </>
  );
}

// ─── Full inline editor table — shared by the takeover and the Manage
// panel (parity: name/description/due/seal/issued/status per row + delete). ───
export function DeliverableEditorTable({
  ctx,
  open,
}: {
  ctx: ProjectWidgetCtx;
  /** When rendered inside a Sheet that stays mounted across close/open
   *  (focus-restore fix), resets the confirm-delete state each time the
   *  panel opens. Omit when used inline in the always-visible takeover. */
  open?: boolean;
}) {
  const [pendingDelete, setPendingDelete] = useState<Deliverable | null>(null);
  useEffect(() => {
    if (open) setPendingDelete(null);
  }, [open]);
  const deliverables = ctx.deliverables.data;
  const today = todayIso();
  const canEdit = ctx.can.edit;
  // The server's delete gate for deliverables is `projects.delete`, not
  // `projects.edit` — the client matches it exactly (docs/ENGINEERING.md
  // permissions law: client gate mirrors server enforcement).
  const canDelete = ctx.can.deleteProject;

  const sorted = useMemo(
    () =>
      [...deliverables].sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999')),
    [deliverables]
  );

  const patch = (d: Deliverable, p: Partial<Deliverable>) => ctx.saveDeliverable({ ...d, ...p });

  return (
    <div>
      <div style={sectionLabel}>All deliverables</div>
      {sorted.length === 0 && (
        <div style={{ ...subText, fontStyle: 'italic', padding: 'var(--space-1) 0' }}>
          No deliverables yet. Add the first one below.
        </div>
      )}
      {sorted.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
          }}
        >
          {sorted.map((d) => {
            const dl = dueLabel(d, today);
            const isComplete = d.status === 'complete';
            return (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <button
                  onClick={
                    canEdit
                      ? () => patch(d, { status: isComplete ? 'pending' : 'complete' })
                      : undefined
                  }
                  disabled={!canEdit}
                  title={isComplete ? 'Mark pending' : 'Mark complete'}
                  aria-label={`${d.name}: ${isComplete ? 'mark pending' : 'mark complete'}`}
                  style={{
                    width: rem(16),
                    height: rem(16),
                    borderRadius: 'var(--radius-full)',
                    cursor: canEdit ? 'pointer' : 'default',
                    flexShrink: 0,
                    background: isComplete ? COLOR_OK : 'transparent',
                    border: `2px solid ${isComplete ? COLOR_OK : statusColor(d, today)}`,
                    padding: 0,
                  }}
                />
                <Input
                  defaultValue={d.name}
                  disabled={!canEdit}
                  aria-label="Deliverable name"
                  onBlur={(e) => {
                    if (e.target.value.trim() && e.target.value !== d.name)
                      patch(d, { name: e.target.value.trim() });
                  }}
                  style={{
                    flex: '1 1 12rem',
                    minWidth: 0,
                    height: rem(30),
                    textDecoration: isComplete ? 'line-through' : 'none',
                  }}
                />
                <Input
                  defaultValue={d.description || ''}
                  disabled={!canEdit}
                  placeholder="Description"
                  aria-label={`${d.name} description`}
                  onBlur={(e) => {
                    if (e.target.value !== (d.description || ''))
                      patch(d, { description: e.target.value || undefined });
                  }}
                  style={{ flex: '1 1 10rem', minWidth: 0, height: rem(30) }}
                />
                <Input
                  type="date"
                  defaultValue={d.dueDate || ''}
                  disabled={!canEdit}
                  aria-label={`${d.name} due date`}
                  onChange={(e) => patch(d, { dueDate: e.target.value || undefined })}
                  style={{ height: rem(30), color: dl.bold ? 'var(--dg)' : undefined }}
                />
                <Input
                  type="date"
                  defaultValue={d.sealDate || ''}
                  disabled={!canEdit}
                  aria-label={`${d.name} seal date`}
                  onChange={(e) => patch(d, { sealDate: e.target.value || undefined })}
                  style={{ height: rem(30) }}
                />
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: rem(4),
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--tm)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={d.issued}
                    disabled={!canEdit}
                    aria-label={`${d.name} issued`}
                    onChange={(e) => patch(d, { issued: e.target.checked })}
                  />
                  Issued
                </label>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: dl.color,
                    fontWeight: dl.bold ? 700 : 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dl.text}
                </span>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${d.name}`}
                    onClick={() => setPendingDelete(d)}
                  >
                    <Icon icon={X} size="sm" className="text-danger" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <QuickAddDeliverable ctx={ctx} />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete deliverable?"
        description={`"${pendingDelete?.name}" will be permanently deleted.`}
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!pendingDelete) return;
          await ctx.deleteDeliverable(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

// ─── Takeover — stats + Gantt timeline + the full editor table, moved
// verbatim from the old expanded body (:294-503, :537-704). ───
function DeliverablesTakeover({ ctx }: { ctx: ProjectWidgetCtx }) {
  const deliverables = ctx.deliverables.data;
  const today = todayIso();
  const sorted = [...deliverables].sort((a, b) =>
    (a.dueDate || '9999').localeCompare(b.dueDate || '9999')
  );
  const overdue = sorted.filter(
    (d) => d.status !== 'complete' && d.dueDate && daysBetween(today, d.dueDate) < 0
  );
  const upcoming = sorted.filter(
    (d) => d.status !== 'complete' && (!d.dueDate || daysBetween(today, d.dueDate) >= 0)
  );
  const complete = sorted.filter((d) => d.status === 'complete');
  const issuedCount = deliverables.filter((d) => d.issued).length;
  const dated = sorted.filter((d) => d.dueDate);

  const timeline = (() => {
    if (dated.length === 0) return null;
    const times = dated.flatMap((d) => {
      const t = [new Date(d.dueDate!).getTime()];
      if (d.sealDate) t.push(new Date(d.sealDate).getTime());
      return t;
    });
    const todayMs = new Date(today).getTime();
    let lo = Math.min(...times, todayMs);
    let hi = Math.max(...times, todayMs);
    const pad = Math.max((hi - lo) * 0.06, 7 * 864e5);
    lo -= pad;
    hi += pad;
    const span = hi - lo || 1;
    const pct = (ms: number) => ((ms - lo) / span) * 100;
    const ticks: { x: number; label: string }[] = [];
    const t = new Date(lo);
    t.setDate(1);
    t.setHours(0, 0, 0, 0);
    let guard = 0;
    while (t.getTime() <= hi && guard++ < 60) {
      const x = pct(t.getTime());
      if (x >= -1 && x <= 101)
        ticks.push({ x, label: t.toLocaleDateString(undefined, { month: 'short' }) });
      t.setMonth(t.getMonth() + 1);
    }
    const laneH = 26;
    const tx = pct(todayMs);
    return (
      <div>
        <div style={sectionLabel}>Timeline</div>
        <div
          style={{
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg)',
            padding: 'var(--space-4) var(--space-2) var(--space-2) 0',
            overflow: 'hidden',
          }}
        >
          <div
            style={{ position: 'relative', marginLeft: rem(140), height: dated.length * laneH + 6 }}
          >
            {ticks.map((tk, i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: tk.x + '%',
                  top: -12,
                  bottom: 0,
                  borderLeft: '1px dashed var(--bd)',
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    left: rem(3),
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--tm)',
                  }}
                >
                  {tk.label}
                </span>
              </div>
            ))}
            {tx >= 0 && tx <= 100 && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: tx + '%',
                  top: -12,
                  bottom: 0,
                  borderLeft: `2px solid ${COLOR_DANGER}`,
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -11,
                    left: -14,
                    fontSize: 'var(--font-size-xs)',
                    color: COLOR_DANGER,
                    fontWeight: 700,
                  }}
                >
                  TODAY
                </span>
              </div>
            )}
            {dated.map((d, i) => {
              const c = statusColor(d, today);
              const top = i * laneH;
              const dueMs = new Date(d.dueDate!).getTime();
              const sealMs = d.sealDate ? new Date(d.sealDate).getTime() : null;
              const barStart = sealMs !== null ? Math.min(sealMs, dueMs) : null;
              const barEnd = sealMs !== null ? Math.max(sealMs, dueMs) : null;
              return (
                <React.Fragment key={d.id}>
                  <div
                    onClick={() => ctx.onDrill({ kind: 'deliverable', deliverableId: d.id })}
                    role="button"
                    tabIndex={0}
                    aria-label={`${d.name}, due ${shortDate(d.dueDate!)}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        ctx.onDrill({ kind: 'deliverable', deliverableId: d.id });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: -140,
                      width: rem(132),
                      top,
                      height: laneH,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: rem(7),
                        height: rem(7),
                        borderRadius: 'var(--radius-full)',
                        background: c,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: d.status === 'complete' ? 400 : 500,
                        color: d.status === 'complete' ? 'var(--tm)' : 'var(--tx)',
                        textDecoration: d.status === 'complete' ? 'line-through' : 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {d.name}
                    </span>
                  </div>
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: top + laneH / 2 - 0.5,
                      height: rem(1),
                      background: 'var(--bd)',
                      opacity: 0.4,
                    }}
                  />
                  {barStart !== null && barEnd !== null && barEnd > barStart && (
                    <div
                      title={`${d.name} · seal ${shortDate(d.sealDate!)} → due ${shortDate(d.dueDate!)}`}
                      style={{
                        position: 'absolute',
                        left: pct(barStart) + '%',
                        width: Math.max(pct(barEnd) - pct(barStart), 0.6) + '%',
                        top: top + laneH / 2 - 4,
                        height: 'var(--space-2)',
                        minWidth: 'var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        background: `color-mix(in srgb, ${c} 30%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${c} 55%, transparent)`,
                      }}
                    />
                  )}
                  <div
                    title={`${d.name} · ${shortDate(d.dueDate!)}`}
                    onClick={() => ctx.onDrill({ kind: 'deliverable', deliverableId: d.id })}
                    style={{
                      position: 'absolute',
                      left: `calc(${pct(dueMs)}% - ${rem(5)})`,
                      top: top + laneH / 2 - 5,
                      width: rem(10),
                      height: rem(10),
                      transform: 'rotate(45deg)',
                      background: c,
                      border: '1.5px solid var(--sf)',
                      cursor: 'pointer',
                      borderRadius: rem(1),
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={statRow}>
        <div>
          <div style={{ ...bigNum, color: overdue.length > 0 ? COLOR_DANGER : 'var(--tx)' }}>
            {overdue.length}
          </div>
          <div style={subText}>overdue</div>
        </div>
        <div>
          <div style={bigNum}>{upcoming.length}</div>
          <div style={subText}>upcoming</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_OK }}>{complete.length}</div>
          <div style={subText}>complete</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_INFO }}>{issuedCount}</div>
          <div style={subText}>issued</div>
        </div>
      </div>
      {timeline ||
        (deliverables.length > 0 ? (
          <div style={{ ...subText, fontStyle: 'italic' }}>
            Add due dates to plot deliverables on the timeline.
          </div>
        ) : null)}
      <DeliverableEditorTable ctx={ctx} />
    </div>
  );
}

export const deliverablesWidget: ProjectWidgetDef = {
  id: 'deliverables',
  title: 'Deliverables',
  category: 'schedule',
  required: true,
  inputSurface: true,
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  render: (ctx) => <DeliverablesCard ctx={ctx} />,
  takeover: (ctx) => <DeliverablesTakeover ctx={ctx} />,
  takeoverTitle: 'Deliverables',
};
